// What-If Panel — PSD §7.3 (F7.3). Analysis tab. Read-only (never mutates persisted valuation).

import { useState, useEffect, useMemo } from 'react';
import type { ValuationInputs, AllocationAssumptions, DiscountInputs } from '@backsolve/shared-types';
import { runValuation } from '@backsolve/shared-logic/runValuation';

interface WhatIfPanelProps {
  inputs: ValuationInputs;
}

type Override = Partial<AllocationAssumptions & DiscountInputs>;

export function WhatIfPanel({ inputs }: WhatIfPanelProps) {
  const [override, setOverride] = useState<Override>({});
  const [debounced, setDebounced] = useState<Override>({});

  // 150ms debounce
  useEffect(() => {
    const t = setTimeout(() => setDebounced(override), 150);
    return () => clearTimeout(t);
  }, [override]);

  const result = useMemo(() => {
    const variant: ValuationInputs = {
      ...inputs,
      assumptions: { ...inputs.assumptions, ...debounced },
      discounts: { ...inputs.discounts, ...debounced },
    };
    try {
      return runValuation(variant);
    } catch {
      return null;
    }
  }, [inputs, debounced]);

  return (
    <div className="what-if-panel">
      <h3>What-If Analysis</h3>
      <p className="field-hint">
        Adjust assumptions to see the impact in real-time. This does not modify your saved valuation.
      </p>

      <div className="what-if-controls">
        <div className="form-group">
          <label htmlFor="wi-vol">Volatility: {((override.volatility ?? inputs.assumptions.volatility) * 100).toFixed(0)}%</label>
          <input
            id="wi-vol"
            type="range"
            min="0.1"
            max="1.5"
            step="0.05"
            value={override.volatility ?? inputs.assumptions.volatility}
            onChange={(e) => setOverride({ ...override, volatility: parseFloat(e.target.value) })}
          />
        </div>

        <div className="form-group">
          <label htmlFor="wi-ttl">
            Time to Liquidity: {(override.timeToLiquidity ?? inputs.assumptions.timeToLiquidity).toFixed(1)}y
          </label>
          <input
            id="wi-ttl"
            type="range"
            min="0.5"
            max="10"
            step="0.5"
            value={override.timeToLiquidity ?? inputs.assumptions.timeToLiquidity}
            onChange={(e) => setOverride({ ...override, timeToLiquidity: parseFloat(e.target.value) })}
          />
        </div>

        <div className="form-group">
          <label htmlFor="wi-dlom">DLOM %: {((override.dlomPercentage ?? inputs.discounts.dlomPercentage) * 100).toFixed(0)}%</label>
          <input
            id="wi-dlom"
            type="range"
            min="0"
            max="0.6"
            step="0.05"
            value={override.dlomPercentage ?? inputs.discounts.dlomPercentage}
            onChange={(e) => setOverride({ ...override, dlomPercentage: parseFloat(e.target.value) })}
          />
        </div>
      </div>

      <button className="btn btn-ghost" onClick={() => setOverride({})}>
        Reset
      </button>

      {result && (
        <div className="what-if-results mt-4">
          <p className="result-caption">
            Re-runs the engine with the slider overrides applied. Numbers update
            in real time and reflect the same waterfall + DLOM as the saved
            valuation.
          </p>
          <div className="headline-grid">
            <div className="metric">
              <span className="metric-label">FMV / share</span>
              <span className="metric-value">${result.headline.commonFmvPerShare.toFixed(4)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Strike Price</span>
              <span className="metric-value">${result.headline.recommendedStrikePrice.toFixed(2)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Common Value</span>
              <span className="metric-value">${result.headline.commonValuePostDlom.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
